/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import OrderDetailsModal from '../modals/OrderDetailsModal';
import OrderStatus from '../orders/OrderStatus';
import Loader from '../util/Loader';
import ReorderIcon from '../svg/ReorderIcon';
import { formatPrice } from '@/utils/misc';
import { reorderItems } from '@/utils/orderUtils';
import { formatDateTime } from '@/utils/dateFormat';
import generalSettingsStore from '@/stores/generalSettingsStore';

function MyOrders({ userData }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderLoading, setReorderLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const ordersPerPage = 10;
  const t = useTranslations('Account');
  const dateFormat = generalSettingsStore.dateFormat;

  const handleReorder = async (order) => {
    try {
      setReorderLoading(order.id);
      await reorderItems(order);
    } catch (error) {
      console.error('Error restoring order:', error);
    } finally {
      setReorderLoading(null);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setPageLoading(true);
        const token = localStorage.getItem('token');  // Get token from localStorage
        const response = await fetch(`/api/shop/order?page=${currentPage}&per_page=${ordersPerPage}`, {
          headers: {
            'Authorization': `Bearer ${token}`,  // Pass token in the Authorization header
          },
        });

        const data = await response.json();
        setOrders(data.orders || []);
        setTotalPages(data.total_pages || 1);
        setLoading(false);
        setPageLoading(false);
        return data;
      } catch (error) {
        console.error('Error fetching orders:', error);
        setLoading(false); // Make sure to stop loading on error as well
        setPageLoading(false);
        return [];
      }
    };

    if (userData) {
      fetchOrders();
    }
  }, [userData, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      // Scroll to top when changing pages
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setCurrentPage(newPage);
    }
  };

  if (loading) return <div className="p-3 relative"><Loader /></div>;

  return (
    <section className="p-3">
      <div className="py-8 px-4 mx-auto max-w-2xl lg:py-0">
        <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">
          {t('my_orders')}
        </h2>
        <hr className="dotted-spaced my-4" />
        {Array.isArray(orders) && orders.length === 0 ? (
          <p>{t('no_orders_found')}</p>
        ) : (
          <>
            <div className="overflow-x-auto relative">
              {pageLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 z-10">
                  <Loader />
                </div>
              )}
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('order_number')}</th>
                    <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('order_date')}</th>
                    <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('order_status')}</th>
                    <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('order_total')}</th>
                    <th className="border-b font-medium p-2 pl-6 pt-0 pb-3 text-start">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(orders) && orders.map((order) => (
                    <tr key={order.id}>
                      <td className="border-gray-100 p-2 pl-6">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="text-primary-studio hover:underline focus:outline-none"
                          title={t('view_order_details')}
                          aria-label={`${t('view_order_details')} #${order.number}`}
                        >
                          #{order.number}
                        </button>
                      </td>
                      <td className="border-gray-100 p-2 pl-6">{formatDateTime(order.date_created, dateFormat)}</td>
                      <td className="border-gray-100 p-2 pl-6"><OrderStatus status={order.status} /></td>
                      <td className="border-gray-100 p-2 pl-6">{formatPrice(order.total)}</td>
                      <td className="border-gray-100 p-2 pl-6">
                        <button
                          onClick={() => handleReorder(order)}
                          disabled={reorderLoading === order.id}
                          className={`flex flex-col items-center justify-center p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-studio focus:ring-offset-2 ${reorderLoading === order.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={t('reorder')}
                        >
                          {reorderLoading === order.id ? (
                            <>
                              <Loader size="sm" />
                              <span className="mt-1 text-xs">{t('loading')}</span>
                            </>
                          ) : (
                            <>
                              <ReorderIcon width={20} height={20} color="#4F46E5" />
                              <span className="mt-1 text-xs text-gray-600">{t('reorder')}</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-8 space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md border ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  aria-label={t('previous_page')}
                >
                  &laquo;
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show max 5 page buttons
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-1 rounded-md ${currentPage === pageNumber ? 'bg-primary-studio text-white' : 'hover:bg-gray-100'}`}
                        aria-label={`${t('page')} ${pageNumber}`}
                        aria-current={currentPage === pageNumber ? 'page' : undefined}
                      >
                        {pageNumber}
                      </button>
                    );
                  }

                  // Add ellipsis
                  if (pageNumber === 2 && currentPage > 3) {
                    return <span key="ellipsis-start" className="px-1">...</span>;
                  }

                  if (pageNumber === totalPages - 1 && currentPage < totalPages - 2) {
                    return <span key="ellipsis-end" className="px-1">...</span>;
                  }

                  return null;
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md border ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  aria-label={t('next_page')}
                >
                  &raquo;
                </button>
              </div>
            )}
          </>
        )}
        {selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </section>
  );
}

export default MyOrders;
